import React from 'react';
import { Avatar } from 'antd';
import { CalendarIcon, LocationMarkerIcon, DefaultAvatar, PlusCircleIcon, BandGroupIcon } from '../../icons';
import { PlayButtonLarge } from '../../PlayButton/PlayButtonLarge';
import { MarqueeText } from '../../MarqueeText/MarqueeText';
import { useAudioPlayer } from '../../../contexts/AudioPlayerContext';
import './ShowListing.css';

export interface BandMember {
  name: string;
  avatarUrl?: string;
}

export interface ShowListingProps {
  bandName: string;
  withText?: string;
  withMembers?: BandMember[];
  date: string;
  description?: string;
  genres?: string[];
  venue: string;
  time?: string;
  creator?: string;
  creatorType?: 'bandMember' | 'showContributor';
  imageUrl?: string;
  soundcloudUrl?: string;
  onClick?: () => void;
}

export const ShowListing: React.FC<ShowListingProps> = ({
  bandName,
  withText,
  withMembers,
  date,
  description,
  genres,
  venue,
  time,
  creator,
  creatorType,
  imageUrl,
  soundcloudUrl,
  onClick,
}) => {
  // Use provided band members if available
  const displayMembers = withMembers && withMembers.length > 0 ? withMembers : null;

  // Audio player context
  const { state, toggle, getProgress } = useAudioPlayer();

  const isPlaying = soundcloudUrl ? state.currentUrl === soundcloudUrl && state.isPlaying : false;
  const isLoading = soundcloudUrl ? state.currentUrl === soundcloudUrl && state.isLoading : false;
  const progress = soundcloudUrl ? getProgress(soundcloudUrl) : 0;

  const handlePlayClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (soundcloudUrl) {
      toggle(soundcloudUrl);
    }
  };

  // Only show play button if there's a soundcloud URL
  const showPlayButton = !!soundcloudUrl;

  return (
    <div className="show-listing" onClick={onClick}>
      <div
        className="show-listing__left"
        style={{
          backgroundImage: imageUrl
            ? `linear-gradient(0deg, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.2) 100%), url(${imageUrl})`
            : 'linear-gradient(0deg, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.2) 100%)'
        }}
      >
        {!imageUrl && (
          <div className="show-listing__empty-icon">
            <BandGroupIcon size={48} />
          </div>
        )}
        {showPlayButton && (
          <div className="show-listing__play-button">
            <PlayButtonLarge
              size={48}
              isPlaying={isPlaying}
              isLoading={isLoading}
              progress={progress}
              onClick={handlePlayClick}
            />
          </div>
        )}
        {time && (
          <div className="show-listing__time-wrapper">
            <div className="show-listing__time">{time}</div>
          </div>
        )}
      </div>
      <div className="show-listing__content">
        <div className="show-listing__header">
          <MarqueeText className="show-listing__primary" gap={32} speed={25}>
            {bandName}
          </MarqueeText>
          <div className="show-listing__with">
            {displayMembers ? (
              <>
                <Avatar.Group size="small" max={{ count: 3 }}>
                  {displayMembers.map((member, index) => (
                    <Avatar
                      key={index}
                      src={member.avatarUrl}
                      size="small"
                      icon={!member.avatarUrl ? <DefaultAvatar size={16} /> : undefined}
                    />
                  ))}
                </Avatar.Group>
                <span className="show-listing__with-text">
                  with {displayMembers.map(m => m.name).join(', ')}
                </span>
              </>
            ) : withText ? (
              <span className="show-listing__with-text">{withText}</span>
            ) : null}
          </div>
        </div>
        <div className="show-listing__body">
          <div className="show-listing__date">
            <span className="show-listing__date-icon">
              <CalendarIcon size="xs" color="#404040" style={{ width: '14px', height: '14px' }} />
            </span>
            <span>{date}</span>
          </div>
          {description && (
            <div className="show-listing__description">
              {description}
            </div>
          )}
          {genres && genres.length > 0 && (
            <div className="show-listing__genres">
              {genres.map((genre) => (
                <span key={genre} className="show-listing__genre-pill">{genre}</span>
              ))}
            </div>
          )}
        </div>
        <div className="show-listing__footer">
          <div className="show-listing__venue">
            <span className="show-listing__venue-icon">
              <LocationMarkerIcon size="xs" color="#404040" style={{ width: '14px', height: '14px' }} />
            </span>
            <span>{venue}</span>
          </div>
          <div className="show-listing__creator">
            <span className="show-listing__creator-icon">
              <PlusCircleIcon width={12} height={12} style={{ color: '#404040' }} />
            </span>
            <span>{creator || bandName}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
